import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { financialPlanningPages } from "@app/const/route.const";
import {
  useGetMeetingGuidanceQuery,
  useGetMeetingGuidanceSignalCatalogQuery,
} from "@app/features/meetingGuidance";
import type { MeetingGuidanceSignalCatalogEntry } from "@app/features/meetingGuidance";

const formatLabel = (value: string | null | undefined) =>
  value
    ? value
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ")
    : "None";

const signalMatchesSearch = (
  signal: MeetingGuidanceSignalCatalogEntry,
  searchTerm: string,
) => {
  if (!searchTerm) {
    return true;
  }
  const searchableText = [
    signal.signal_id,
    signal.label,
    signal.description,
    signal.source,
    signal.signal_group,
    signal.trigger_summary,
    ...signal.source_fields,
    ...signal.impacts.flatMap((impact) => [
      impact.taxonomy_topic_id,
      impact.taxonomy_subtopic_id,
      impact.ai_question_domain,
      impact.agenda_item_id,
      ...impact.fallback_template_ids,
      impact.explanation,
    ]),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return searchableText.includes(searchTerm.toLowerCase());
};

export const MeetingGuidanceSignalCatalogPage = () => {
  const { planResultRootId, planResultTypeId } = useParams<{
    planResultRootId: string;
    planResultTypeId: string;
  }>();
  const navigate = useNavigate();
  const rootId = planResultRootId ?? "";
  const typeId = planResultTypeId ?? "";
  const [searchTerm, setSearchTerm] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeMeetingFilter, setActiveMeetingFilter] = useState("all");
  const [expandedSignalId, setExpandedSignalId] = useState<string | null>(null);

  const {
    data: catalogData,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    error: catalogError,
    refetch: refetchCatalog,
  } = useGetMeetingGuidanceSignalCatalogQuery(
    {
      planResultRootId: rootId,
      planResultTypeId: typeId,
    },
    { skip: !planResultRootId || !planResultTypeId },
  );
  const {
    data: guidanceData,
    isLoading: isGuidanceLoading,
    isError: isGuidanceError,
  } = useGetMeetingGuidanceQuery(
    {
      planResultRootId: rootId,
      planResultTypeId: typeId,
    },
    { skip: !planResultRootId || !planResultTypeId },
  );

  const activeSignalIds = useMemo(() => {
    const ids = new Set<string>();
    guidanceData?.signal_explanations.activated_signals.forEach((signal) => {
      ids.add(signal.signal_id);
    });
    guidanceData?.signal_explanations.question_evidence.forEach((evidence) => {
      evidence.source_signal_ids.forEach((signalId) => ids.add(signalId));
    });
    return ids;
  }, [guidanceData]);

  const sourceOptions = useMemo(
    () =>
      Array.from(new Set(catalogData?.signals.map((signal) => signal.source) ?? []))
        .filter(Boolean)
        .sort(),
    [catalogData],
  );
  const groupOptions = useMemo(
    () =>
      Array.from(
        new Set(catalogData?.signals.map((signal) => signal.signal_group) ?? []),
      )
        .filter(Boolean)
        .sort(),
    [catalogData],
  );

  const filteredSignals = useMemo(() => {
    return (catalogData?.signals ?? []).filter((signal) => {
      const isActiveInMeeting = activeSignalIds.has(signal.signal_id);
      return (
        signalMatchesSearch(signal, searchTerm) &&
        (sourceFilter === "all" || signal.source === sourceFilter) &&
        (groupFilter === "all" || signal.signal_group === groupFilter) &&
        (statusFilter === "all" ||
          (statusFilter === "active" && signal.is_active) ||
          (statusFilter === "inactive" && !signal.is_active) ||
          (statusFilter === "configurable" && signal.is_configurable)) &&
        (activeMeetingFilter === "all" ||
          (activeMeetingFilter === "active" && isActiveInMeeting) ||
          (activeMeetingFilter === "not_active" && !isActiveInMeeting))
      );
    });
  }, [
    activeMeetingFilter,
    activeSignalIds,
    catalogData,
    groupFilter,
    searchTerm,
    sourceFilter,
    statusFilter,
  ]);

  if (!planResultRootId || !planResultTypeId) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="par02Regular">
          Missing required parameters: planResultRootId and planResultTypeId
        </Typography>
      </Box>
    );
  }

  const catalogErrorMessage = String(
    (catalogError as { data?: { error?: string } } | undefined)?.data?.error ??
      "Failed to load meeting guidance signal catalog.",
  );
  const totalSignals = catalogData?.signals.length ?? 0;
  const activeSignals = catalogData?.signals.filter((signal) => signal.is_active).length ?? 0;
  const configurableSignals =
    catalogData?.signals.filter((signal) => signal.is_configurable).length ?? 0;

  return (
    <Box sx={{ width: "100vw", minHeight: "100vh", backgroundColor: "grey.100" }}>
      <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 3 }}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "flex-start" }}
            >
              <Stack spacing={1}>
                <Typography variant="h3SMedium">Signal Catalog</Typography>
                <Typography variant="par02Regular" color="text.secondary">
                  Review every available meeting-guidance signal and inspect how each
                  signal can influence evidence, domains, agenda areas, and fallback
                  questions.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip label={`Registry: ${catalogData?.registry_version ?? "loading"}`} />
                  <Chip label={`${totalSignals} signals`} />
                  <Chip label={`${activeSignals} active`} />
                  <Chip label={`${configurableSignals} configurable`} />
                  <Chip label={`${activeSignalIds.size} active in this meeting`} />
                </Stack>
              </Stack>
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(
                    financialPlanningPages.getMeetingGuidanceUrl(
                      planResultRootId,
                      planResultTypeId,
                    ),
                  )
                }
              >
                Back to Meeting Guidance
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {isCatalogError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => refetchCatalog()}>
                Retry
              </Button>
            }
          >
            {catalogErrorMessage}
          </Alert>
        )}
        {isGuidanceError && (
          <Alert severity="warning">
            Current-meeting signals could not be loaded. The full catalog is still
            available, but active-in-meeting highlighting may be incomplete.
          </Alert>
        )}

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Search signals"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="signal-source-filter-label">Source</InputLabel>
                  <Select
                    labelId="signal-source-filter-label"
                    label="Source"
                    value={sourceFilter}
                    onChange={(event) => setSourceFilter(event.target.value)}
                  >
                    <MenuItem value="all">All sources</MenuItem>
                    {sourceOptions.map((source) => (
                      <MenuItem key={source} value={source}>
                        {formatLabel(source)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="signal-group-filter-label">Group</InputLabel>
                  <Select
                    labelId="signal-group-filter-label"
                    label="Group"
                    value={groupFilter}
                    onChange={(event) => setGroupFilter(event.target.value)}
                  >
                    <MenuItem value="all">All groups</MenuItem>
                    {groupOptions.map((group) => (
                      <MenuItem key={group} value={group}>
                        {formatLabel(group)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <FormControl sx={{ minWidth: 180 }}>
                  <InputLabel id="signal-status-filter-label">Status</InputLabel>
                  <Select
                    labelId="signal-status-filter-label"
                    label="Status"
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                  >
                    <MenuItem value="all">All statuses</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="configurable">Configurable</MenuItem>
                  </Select>
                </FormControl>
                <FormControl sx={{ minWidth: 220 }}>
                  <InputLabel id="meeting-active-filter-label">
                    Current Meeting
                  </InputLabel>
                  <Select
                    labelId="meeting-active-filter-label"
                    label="Current Meeting"
                    value={activeMeetingFilter}
                    onChange={(event) => setActiveMeetingFilter(event.target.value)}
                  >
                    <MenuItem value="all">All signals</MenuItem>
                    <MenuItem value="active">Active in this meeting</MenuItem>
                    <MenuItem value="not_active">Not active in this meeting</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="par02Regular" color="text.secondary">
                    Showing {filteredSignals.length} of {totalSignals}
                    {isCatalogLoading || isGuidanceLoading ? " while loading..." : ""}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Stack spacing={2}>
          {filteredSignals.map((signal) => {
            const isActiveInMeeting = activeSignalIds.has(signal.signal_id);
            const isExpanded = expandedSignalId === signal.signal_id;
            const impactedDomains = Array.from(
              new Set(
                signal.impacts
                  .map((impact) => impact.ai_question_domain)
                  .filter(Boolean) as string[],
              ),
            );
            return (
              <Card key={signal.signal_id}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Stack spacing={0.75}>
                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                          <Chip
                            color={isActiveInMeeting ? "success" : "default"}
                            label={
                              isActiveInMeeting
                                ? "Active in this meeting"
                                : "Not active in this meeting"
                            }
                            size="small"
                          />
                          <Chip
                            color={signal.is_active ? "primary" : "default"}
                            label={signal.is_active ? "Registry active" : "Registry inactive"}
                            size="small"
                          />
                          {signal.is_configurable && (
                            <Chip label="Configurable" size="small" />
                          )}
                          <Chip label={formatLabel(signal.signal_group)} size="small" />
                          <Chip label={formatLabel(signal.source)} size="small" />
                        </Stack>
                        <Typography variant="h5SMedium">{signal.label}</Typography>
                        <Typography variant="par02Regular" color="text.secondary">
                          {signal.signal_id}
                        </Typography>
                        <Typography variant="par02Regular">
                          {signal.description}
                        </Typography>
                      </Stack>
                      <Button
                        variant="outlined"
                        onClick={() =>
                          setExpandedSignalId(isExpanded ? null : signal.signal_id)
                        }
                      >
                        {isExpanded ? "Hide details" : "View details"}
                      </Button>
                    </Stack>

                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                      {impactedDomains.length > 0 ? (
                        impactedDomains.map((domain) => (
                          <Chip key={domain} label={`Domain: ${formatLabel(domain)}`} />
                        ))
                      ) : (
                        <Chip label="No AI domain impact" />
                      )}
                      <Chip label={`${signal.impacts.length} impact(s)`} />
                    </Stack>

                    <Collapse in={isExpanded} unmountOnExit>
                      <Stack spacing={2} sx={{ pt: 1 }}>
                        <Box>
                          <Typography variant="par01SemiBold">
                            Trigger Summary
                          </Typography>
                          <Typography variant="par02Regular">
                            {signal.trigger_summary}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="par01SemiBold">Source Fields</Typography>
                          <Typography variant="par02Regular">
                            {signal.source_fields.join(", ")}
                          </Typography>
                        </Box>
                        <Stack spacing={1}>
                          <Typography variant="par01SemiBold">Impacts</Typography>
                          {signal.impacts.length > 0 ? (
                            signal.impacts.map((impact, index) => (
                              <Box
                                key={`${signal.signal_id}-impact-${index}`}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                  borderRadius: 1,
                                  p: 1.5,
                                }}
                              >
                                <Stack spacing={0.5}>
                                  <Typography variant="par02Regular">
                                    {impact.explanation}
                                  </Typography>
                                  <Typography variant="par02Regular" color="text.secondary">
                                    Topic: {formatLabel(impact.taxonomy_topic_id)} |
                                    Subtopic: {formatLabel(impact.taxonomy_subtopic_id)} |
                                    Agenda: {formatLabel(impact.agenda_item_id)} |
                                    Domain: {formatLabel(impact.ai_question_domain)}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                  >
                                    {impact.creates_question_evidence && (
                                      <Chip label="Creates evidence" size="small" />
                                    )}
                                    {impact.unlocks_domain && (
                                      <Chip label="Unlocks domain" size="small" />
                                    )}
                                    {impact.can_drive_ai_questions && (
                                      <Chip label="Can drive AI questions" size="small" />
                                    )}
                                    {impact.can_drive_fallback_questions && (
                                      <Chip
                                        label="Can drive fallback questions"
                                        size="small"
                                      />
                                    )}
                                  </Stack>
                                  {impact.fallback_template_ids.length > 0 && (
                                    <Typography
                                      variant="par02Regular"
                                      color="text.secondary"
                                    >
                                      Fallback templates:{" "}
                                      {impact.fallback_template_ids.join(", ")}
                                    </Typography>
                                  )}
                                </Stack>
                              </Box>
                            ))
                          ) : (
                            <Typography variant="par02Regular" color="text.secondary">
                              No impacts are registered for this signal.
                            </Typography>
                          )}
                        </Stack>
                      </Stack>
                    </Collapse>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
          {!isCatalogLoading && filteredSignals.length === 0 && (
            <Alert severity="info">No signals match the selected filters.</Alert>
          )}
        </Stack>
      </Box>
    </Box>
  );
};
